// FIXME no-param-reassign

/* eslint-disable no-param-reassign */
const PROTOCOL_REGEX = /^https?:\/\//;
const WITH_PROTOCOL_REGEX = /^https?:\/\/[^ "]+$/;
const WITH_HTTPS_PROTOCOL_REGEX = /^https:\/\/[^ "]+$/;

/**
 * Validate a URL as being an IPv6 address.
 * Taken from Stack Overflow: https://stackoverflow.com/a/41436860
 * This is used instead of a regex because most (reasonably comprehensive) regexes are between 500 and 1500
 * characters long.
 *
 * @param {string} urlToValidate The Url to validate as IPv6
 * @returns {boolean} True if valid, False otherwise
 */
export const isIPv6 = (urlToValidate: string): boolean => {
  let url = urlToValidate;
  if (url.startsWith('http://')) {
    url = url.slice(7);
  } else if (url.startsWith('https://')) {
    url = url.slice(8);
  }

  if (url.includes('/')) {
    url = url.slice(0, url.indexOf('/'));
  }

  if (url.startsWith('[') && url.includes(']')) {
    url = url.slice(1, url.indexOf(']'));
  }

  // Check if there are more then 2 : together (ex. :::)
  if (/:{3,}/.test(url)) {
    return false;
  }
  // Check if there are more then 2 :: (ex. ::2001::)
  if (/::.+::/.test(url)) {
    return false;
  }
  // Check if there is a single : at the end (requires :: if any)
  if (/[^:]:$/.test(url)) {
    return false;
  }
  // Check for leading colon
  if (/^:(?!:)/.test(url)) {
    return false;
  }
  // Split all the part to check each
  const ipv6Parts = url.split(':');
  // Make sure there are at lease 2 parts and no more then 8
  if (ipv6Parts.length < 2 || ipv6Parts.length > 8) {
    return false;
  }

  // All parts must be valid for this to be a valid IPv6 address
  return ipv6Parts.reduce((memo: boolean, part: string): boolean => {
    return memo && /^[0-9a-fA-F]{0,4}$/.test(part);
  }, true);
};

/**
 * Validate a URL.
 *
 * @param url Url to validate.
 * @param {boolean} requireProtocol Require the protocol (http/https) to be part of the URL.
 * @returns {boolean} True if valid, False otherwise
 */
export const validateUrl = (url: string, requireProtocol?: boolean): boolean => {
  let normalizedUrl = url;

  if (requireProtocol && !WITH_PROTOCOL_REGEX.test(url)) {
    return false;
  }

  if (!requireProtocol && !WITH_PROTOCOL_REGEX.test(url)) {
    normalizedUrl = `https://${url}`;
  }

  try {
    const urlObject = new URL(normalizedUrl);
    return !!urlObject;
  } catch {
    return isIPv6(url);
  }
};

/**
 * Validate an HTTPS URL.
 *
 * @param url Url to validate.
 * @returns {boolean} True if valid, False otherwise
 */
export const validateHTTPSUrl = (url: string): boolean => {
  if (WITH_PROTOCOL_REGEX.test(url) && !WITH_HTTPS_PROTOCOL_REGEX.test(url)) {
    return false;
  }

  return validateUrl(url, true);
};

/**
 * Normalize a URL by adding http protocol if the URL doesn't specify a protocol.
 * This works on host names and IP addresses (v4 & v6)
 *
 * @example
 * // returns 'http://www.hotjar.com/'
 * normalize('www.hotjar.com');
 * @example
 * // returns 'http://www.hotjar.com/'
 * normalize('http://www.hotjar.com');
 * @example
 * // returns 'http://www.hotjar.com/?query=1'
 * normalize('http://www.hotjar.com?query=1');
 * @example
 * // returns 'http://www.hotjar.com/?query=1#123'
 * normalize('http://www.hotjar.com?query=1#123');
 * @param url Url to normalize.
 * @returns {string}
 */
export const normalize = (url: string): string => {
  const normalizedUrl = PROTOCOL_REGEX.test(url) ? url : `http://${url}`;

  if (isIPv6(url)) {
    return normalizedUrl.slice(8).indexOf('/') > -1 ? normalizedUrl : `${normalizedUrl}/`;
  }

  return new URL(normalizedUrl).toString();
};

function updateQuery(uri: string, key: string, value: string | number | boolean) {
  if (value) {
    const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    if (uri.match(re)) {
      return uri.replace(re, `$1${key}=${value}$2`);
    }

    const separator = uri.indexOf('?') !== -1 ? '&' : '?';
    return `${uri}${separator}${key}=${value}`;
  }

  // remove key-value pair if value is empty
  uri = uri.replace(new RegExp(`([?&]?)${key}=[^&]*`, 'i'), '');

  if (uri.slice(-1) === '?') {
    uri = uri.slice(0, -1);
  }

  // replace first occurrence of & by ? if no ? is present
  if (uri.indexOf('?') === -1) {
    uri = uri.replace(/&/, '?');
  }

  return uri;
}

function splitUriAndHash(uriToSplit: string) {
  const hashIndex = uriToSplit.indexOf('#');
  if (hashIndex === -1) {
    return [uriToSplit, ''];
  }

  const hash = uriToSplit.substr(hashIndex);
  const uri = uriToSplit.substr(0, hashIndex);

  return [uri, hash];
}

const updateUrlParameter = (uriToUpdate: string, key: string, value: string | number | boolean) => {
  const [uri, hash] = splitUriAndHash(uriToUpdate);
  return updateQuery(uri, key, value) + hash;
};

export const updateUrlParameters = (
  uri: string,
  keyValuePairs: {
    [key: string]: string | number | boolean;
  },
): string =>
  Object.keys(keyValuePairs).reduce((updatedURI, key) => {
    return updateUrlParameter(updatedURI, key, keyValuePairs[key]);
  }, uri);

export const getParameter = (url: string, name: string): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    return params.get(name) || '';
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Unable to get query param.');
    return '';
  }
};

export const clearParameter = (url: string, name: string): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    params.delete(name);

    return `${urlObj.origin}${urlObj.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Unable to delete query param.');
    return url;
  }
};

export const clearParameters = (url: string, names: string[]): string => {
  let tempUrl = url;

  names.forEach((name) => {
    tempUrl = clearParameter(tempUrl, name);
  });

  return tempUrl;
};

export const setParameter = (url: string, name: string, value: string | null): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    if (value === null || value === '') {
      params.delete(name);
    } else {
      params.set(name, value);
    }

    return `${urlObj.origin}${urlObj.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Unable to set query param.');
    return url;
  }
};

export const setParameters = (url: string, values: { [key: string]: string | null }): string => {
  let tempUrl = url;

  Object.keys(values).forEach((key) => {
    tempUrl = setParameter(tempUrl, key, values[key]);
  });

  return tempUrl;
};

export const getUrlPathAndSearch = (url: string): string => {
  const urlObj = new URL(url);

  return `${urlObj.pathname}${urlObj.search}`;
};
